import { Button, Stack, TypographyProps } from "@mui/material";
import { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import PageTabsV2 from "@/App/Layouts/PageBody/PageTabsV2";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AuthorizedTenantElement from "@/common/components/Auth/AuthorizedTenantElement";
import EditIcon from "@/common/components/Icons/Svg/EditIcon";
import { AppModalCloseIconButton } from "@/common/components/Modals/AppModalCloseIconButton";
import { PersistenceKey } from "@/common/constants/persistenceKey";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { useCommonViewParams } from "@/common/hooks/layout/useCommonViewParams";
import { useRealtimeDataUpdates } from "@/common/hooks/realtime/useRealtimeDataUpdates";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import {
  AccessoryCheckDto,
  AppPermission,
  DataUpdatesHubClientMethodName,
  EntityType,
  VehicleOperationType,
} from "@/core/api/generated/v1";

import { VehicleOperationViewPageHeader } from "../../Vehicle/View/Header/VehicleOperationViewPageHeader";
import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import AccessoryCheckMenu from "../AccessoryCheckMenu";
import CommentsTabContent from "./Tabs/CommentsTabContent";
import ContractTabContent from "./Tabs/ContractTabContent";
import GeneralHistoryTabContent from "./Tabs/GeneralHistoryTabContent";
import OverviewTabContent from "./Tabs/OverviewTabContent";
import VehicleTabContent from "./Tabs/VehicleTabContent";

export enum AccessoryCheckViewPageTabs {
  Overview = "Overview",
  Vehicle = "Vehicle",
  Contract = "Contract",
  GeneralHistory = "GeneralHistory",
  Comments = "Comments",
}
const defaultHeaderProps = {
  withLink: false,
  typographyProps: undefined as Partial<TypographyProps> | undefined,
};

const defaultDisplayProps = {
  breadcrumbs: true,
  actions: true,
  closeInHeader: true,
  vehicle: true,
  contract: true,
  items: true,
  attachments: true,
  viewVariant: ViewVariant.Page,
};

interface OwnProps {
  accessoryCheckId?: string | null;
  accessoryCheck?: AccessoryCheckDto | null;
  withRealtimeDataUpdates?: boolean;
  headerProps?: Partial<typeof defaultHeaderProps> & {
    title?: ReactNode | ((entity?: AccessoryCheckDto | null) => ReactNode);
    titleTypographyProps?: TypographyProps;
  };
  displayProps?: Partial<typeof defaultDisplayProps>;
  onEntityChanged?: () => void;
}

export type AccessoryCheckViewProps = OwnProps & BaseEntityViewInheritableProps<AccessoryCheckDto>;

export default function AccessoryCheckView({
  accessoryCheckId,
  accessoryCheck,
  withRealtimeDataUpdates = true,
  headerProps = defaultHeaderProps,
  displayProps = defaultDisplayProps,
  onEntityChanged,
  onUpdate,
  onDelete,
  onClose,
}: AccessoryCheckViewProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const history = useAppHistory();
  const currentTenant = useCurrentTenant();
  const commonViewParams = useCommonViewParams({
    statePersistence: {
      persistenceKey: PersistenceKey.forEntityView(EntityType.AccessoryCheck),
      viewVariant: displayProps.viewVariant,
    },
  });

  const request = useApiRequest(
    apiClient.accessoryChecksApi.apiV1AccessoryChecksAccessoryCheckIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      accessoryCheckId: accessoryCheckId!,
    },
    {
      deps: [accessoryCheckId],
      skip: !accessoryCheckId,
    },
  );
  accessoryCheck = request?.data || accessoryCheck;

  useRealtimeDataUpdates({
    channelNames: [
      DataUpdatesChannelName.Entity(
        currentTenant?.id,
        EntityType.AccessoryCheck,
        accessoryCheckId || "",
      ),
    ],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      request.handleEntityChanged(data);
    },
  });

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumb:
      (displayProps?.breadcrumbs &&
        accessoryCheck && {
          idValue: accessoryCheck.id!,
          newTitle: accessoryCheck.localNumber || "",
        }) ||
      undefined,
  });

  return (
    <BaseEntityView
      entityType={EntityType.AccessoryCheck}
      entityId={accessoryCheckId}
      entity={accessoryCheck}
      entityRequest={request}
    >
      <ViewLayout
        displayProps={displayProps}
        header={
          accessoryCheck && (
            <VehicleOperationViewPageHeader
              operationType={VehicleOperationType.AccessoryCheck}
              operationEntity={accessoryCheck}
              vehicleId={accessoryCheck?.vehicle?.id}
              vehicle={accessoryCheck?.vehicle || undefined}
              titleProps={{
                to: headerProps?.withLink
                  ? ROUTE_PATH.ACCESSORY_CHECK_VIEW(accessoryCheck?.id)
                  : undefined,
                typographyProps: headerProps?.typographyProps,
              }}
              primaryActions={
                displayProps?.actions && (
                  <Stack direction='row' gap={1}>
                    <AuthorizedTenantElement tenantIds={[accessoryCheck?.tenantId]}>
                      <AuthorizedElement permissions={[AppPermission.AccessoryCheckManage]}>
                        <Button
                          variant='outlined'
                          color='text'
                          component={RouterLink}
                          to={ROUTE_PATH.ACCESSORY_CHECK_EDIT(accessoryCheck?.id)}
                          startIcon={<EditIcon />}
                        >
                          Edit
                        </Button>
                      </AuthorizedElement>
                    </AuthorizedTenantElement>
                  </Stack>
                )
              }
              secondaryActions={
                accessoryCheck && (
                  <AccessoryCheckMenu
                    entity={accessoryCheck}
                    displayProps={{
                      actions: {
                        downloadPdf: true,
                        edit: false,
                        delete: true,
                        addToContract: true,
                        addToAssignment: true,
                      },
                    }}
                    onUpdate={(newValue) => {
                      newValue ? request.replaceData(newValue) : request.refetch();
                      onUpdate?.(newValue);
                    }}
                    onDelete={() => (onDelete ? onDelete() : history.goBack())}
                  />
                )
              }
              closeAction={
                displayProps.closeInHeader && onClose ? (
                  <AppModalCloseIconButton onClick={onClose} />
                ) : undefined
              }
              onTagsUpdated={(newValue) => {
                request.updateData((x) => {
                  x.tags = newValue || undefined;
                });
              }}
            />
          )
        }
      >
        <PageTabsV2
          tabIdsDefinition={AccessoryCheckViewPageTabs}
          defaultTabId={AccessoryCheckViewPageTabs.Overview}
          commonViewParams={commonViewParams}
          tabs={[
            { label: "Overview", value: AccessoryCheckViewPageTabs.Overview, isHideable: false },
            {
              label: "Vehicle",
              value: AccessoryCheckViewPageTabs.Vehicle,
              permissions: [AppPermission.VehicleRead],
            },
            ...(accessoryCheck?.contract?.id
              ? [
                  {
                    label: "Contract",
                    value: AccessoryCheckViewPageTabs.Contract,
                    permissions: [AppPermission.ContractRead],
                  },
                ]
              : []),
            {
              label: "History",
              value: AccessoryCheckViewPageTabs.GeneralHistory,
              permissions: [AppPermission.GeneralHistoryRead],
            },
            { label: "Comments", value: AccessoryCheckViewPageTabs.Comments },
          ]}
        >
          {({ activeTabId: activeTab }) =>
            accessoryCheck && (
              <>
                {activeTab === AccessoryCheckViewPageTabs.Overview && (
                  <OverviewTabContent
                    accessoryCheck={accessoryCheck}
                    onUpdate={(newValue) => {
                      newValue ? request.replaceData(newValue) : request.refetch();
                      onUpdate?.(newValue);
                    }}
                  />
                )}
                {activeTab === AccessoryCheckViewPageTabs.Vehicle && (
                  <VehicleTabContent accessoryCheck={accessoryCheck} />
                )}
                {activeTab === AccessoryCheckViewPageTabs.Contract && (
                  <ContractTabContent accessoryCheck={accessoryCheck} />
                )}
                {activeTab === AccessoryCheckViewPageTabs.GeneralHistory && (
                  <GeneralHistoryTabContent accessoryCheck={accessoryCheck} />
                )}
                {activeTab === AccessoryCheckViewPageTabs.Comments && (
                  <CommentsTabContent accessoryCheck={accessoryCheck} />
                )}
              </>
            )
          }
        </PageTabsV2>
      </ViewLayout>
    </BaseEntityView>
  );
}
